import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { LegalLinks } from '@/components/support-links';
import { AuthShowcase } from './auth-showcase';

/**
 * Shared chrome for every unauthenticated surface (login, signup, password
 * recovery, invite acceptance, OAuth hand-off, /connect, 404). Pages supply
 * only their title, subtitle and content.
 *
 * - `showcase` (login, signup): split screen. The form column on the left
 *   (logo top-left, the form centered at 380px) and, from `lg` up, the
 *   decorative `AuthShowcase` panel on the right.
 * - Otherwise: one card under the logo, for the single-step states.
 * - Below `lg` there is no card and no panel: the content fills the screen
 *   with 20px margins, and `children` is a flex column, so a page can push its
 *   main button to the bottom with `AUTH_BOTTOM` (auth-layout.ts).
 */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  icon,
  align = 'start',
  showcase = false,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Icon tile (`StatusBadge`) or avatar above the heading of a single-step state. */
  icon?: ReactNode;
  /** `start` for forms (a shared left edge); `center` for the single-step states. */
  align?: 'start' | 'center';
  /** Split screen with the decorative panel (login and signup). */
  showcase?: boolean;
}) {
  const centered = align === 'center';
  const content = (
    <>
      {icon && <div className={cn('mb-4', centered && 'flex justify-center')}>{icon}</div>}
      <header className={cn('u360-rise', centered && 'text-center')} style={{ animationDelay: '80ms' }}>
        <h1 className="text-2xl font-bold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{subtitle}</p>}
      </header>
      <div className={cn('mt-6 flex flex-1 flex-col gap-5', centered && 'text-center')}>{children}</div>
    </>
  );
  const after = (
    <>
      {footer && (
        <div className="u360-rise mt-6 text-center" style={{ animationDelay: '300ms' }}>
          {footer}
        </div>
      )}
      <LegalLinks className="u360-rise mt-6" />
    </>
  );

  if (showcase) {
    return (
      <main className="flex min-h-dvh bg-canvas lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex min-h-dvh w-full flex-col px-5 pb-6 pt-5 lg:px-12 lg:pb-8 lg:pt-8">
          <AuthWordmark className="self-start" />
          <div className="flex w-full flex-1 flex-col max-lg:mt-10 lg:mx-auto lg:max-w-[380px] lg:justify-center">
            <div className="flex flex-1 flex-col lg:flex-none">{content}</div>
            {after}
          </div>
        </div>
        <AuthShowcase className="sticky top-3 m-3 hidden h-[calc(100dvh-24px)] lg:block" />
      </main>
    );
  }

  return (
    <main className="flex min-h-dvh flex-col bg-canvas px-5 pb-6 pt-5 lg:items-center lg:justify-center lg:px-4 lg:py-12">
      <AuthWordmark className="self-start lg:mb-6 lg:self-center" />
      <div className="flex w-full flex-1 flex-col max-lg:mt-10 lg:max-w-[400px] lg:flex-none">
        <div className="flex flex-1 flex-col lg:rounded-card lg:border lg:border-line lg:bg-surface lg:p-7 lg:shadow-e3">{content}</div>
        {after}
      </div>
    </main>
  );
}

/** The urbania360 logo of the access pages; links home. */
export function AuthWordmark({ className }: { className?: string }) {
  return (
    <Link
      to="/"
      className={cn(
        'u360-rise flex items-center gap-2.5 rounded-control text-base font-semibold tracking-tight text-ink outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        className,
      )}
    >
      <span className="inline-flex size-8 items-center justify-center rounded-control bg-ink text-sm font-bold text-surface shadow-e1">
        U
      </span>
      <span>
        urbania<span className="text-brand-ink">360</span>
      </span>
    </Link>
  );
}

/* Leads the stagger — the tile renders above the heading, so it has to land
   before it (the header uses 80ms). */
const BADGE_DELAY_MS = '40ms';

const TILE_TONE = {
  brand: 'bg-brand-subtle text-brand-ink',
  danger: 'bg-danger-subtle text-danger',
  neutral: 'bg-surface-sunken text-ink-2',
} as const;

/**
 * Icon tile that opens a single-step state: a rounded square, `brand` for
 * "this worked, here is what happens next", `danger` for "this cannot
 * continue", `neutral` for "nothing here".
 */
export function StatusBadge({ tone = 'brand', children }: { tone?: keyof typeof TILE_TONE; children: ReactNode }) {
  return (
    <span
      data-slot="status-tile"
      className={cn('u360-rise inline-flex size-12 items-center justify-center rounded-card [&_svg]:size-6', TILE_TONE[tone])}
      style={{ animationDelay: BADGE_DELAY_MS }}
      aria-hidden="true"
    >
      {children}
    </span>
  );
}

/** Labelled rule between the OAuth button and the credential form. */
export function OrDivider({ delay = 0 }: { delay?: number }) {
  return (
    <div
      className="u360-rise flex items-center gap-3"
      style={{ animationDelay: `${delay}ms` }}
      aria-hidden="true"
    >
      <span className="h-px flex-1 bg-line" />
      {/* Lowercase on purpose: an uppercased 11px "o" between two rules reads
          as a circle or a degree sign, not as the word. */}
      <span className="text-xs font-medium text-ink-3">o</span>
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

export function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
